/* eslint-disable max-len */
import { ChangeEvent, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { MESSAGES } from '@app/constant';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateImageMutation, useDeleteImageMutation } from '@modules/images';
import { useLazyGetTeamByIdQuery, useCreateTeamMutation, useUpdateTeamMutation } from '@modules/team';
import { useRenderFileViewHook } from '@app/hooks/imagesRender';
import { Image } from '@modules/team/types';

import { IFlowProcess, ITeamFormData, OnChangeFunctionProps } from './helper/types';
import { createTeamSchema, initialFlowState, initialFormState } from './helper/data';
import { onChangeUpload, handleDeleteImage } from './helper/localHelper';
import { prepareClonedData, prepareImageUploadPromises } from './helper/utils';

export const useTeamLogic = () => {
  const { id } = useParams()

  const [createTeam] = useCreateTeamMutation()
  const [updateTeam] = useUpdateTeamMutation()
  const [createImage] = useCreateImageMutation()
  const [deleteImage] = useDeleteImageMutation()
  const [getTeamById] = useLazyGetTeamByIdQuery();
  const [flowProcess, setFlowProcess] = useState<IFlowProcess>(initialFlowState)

  const methods = useForm<ITeamFormData>({
    // @ts-ignore
    resolver: yupResolver(createTeamSchema),
    mode: 'onSubmit',
    defaultValues: initialFormState
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = methods;

  const images = watch('uploadedFileList')
  const existingImagesWatch = watch('existingImages')

  const previews = useRenderFileViewHook(images, existingImagesWatch)

  const handleLoadingState = (isLoading: boolean, successMessage: string = '') => {
    setFlowProcess((prev) => ({
      ...prev,
      isButtonLoading: isLoading,
      isSuccess: successMessage,
      largeImageMessage: '',
    }));
  };

  const onSubmit = async (data: ITeamFormData) => {
    handleLoadingState(true);

    let imagePaths: string[] = [];

    try {
      const imageUploadPromises = prepareImageUploadPromises(data.uploadedFileList, createImage);

      const uploadedImages = await Promise.all(imageUploadPromises || []);

      imagePaths = uploadedImages?.map((result) => result?.id || '');

      const clonedData = prepareClonedData(data, imagePaths);

      if (id) {
        await updateTeam({ id, body: clonedData }).unwrap()
        getTeamData()
      } else {
        await createTeam(clonedData).unwrap();
        reset(initialFormState);
      }

      handleLoadingState(false, id ? MESSAGES.teamHasBeenSuccessfullyUpdated : MESSAGES.teamHasBeenSuccessfullyCreated);
    } catch (error: any) {
      if (imagePaths?.length) {
        // If after first step, when FileList uploaded in backend.
        // And then before team creation appears some problems.
        // In that case we delete all uploaded images

        imagePaths.forEach(async (imageId) => {
          await deleteImage({ id: imageId })
        })
      }

      handleLoadingState(false, error?.data?.message || MESSAGES.somethingWentWrong);
    }
  };

  const getTeamData = async () => {
    try {
      if (!id) {
        setFlowProcess((prev) => ({ ...prev, isLoading: false }))

        return
      }

      const team = await getTeamById(id).unwrap()

      const clonedImages = JSON.parse(JSON.stringify(team?.images)) as Image[]

      clonedImages?.sort((a, b) => a.sort - b.sort);

      reset({
        ...team,
        existingImages: clonedImages,
        mainImageUrl: team.images.find((image) => image.isMain)?.url || null,
      });
    } catch (error) {
      console.log("Error", error);
    } finally {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))
    }
  }

  useEffect(() => {
    getTeamData()
  }, [id])

  return {
    ...flowProcess,
    paramId: id,
    methods,
    errors,
    watch,
    setValue,
    setError,
    setFlowProcess,
    onSubmit,
    register,
    handleSubmit,
    handleDeleteImage: (bas64: string) => handleDeleteImage({
      bas64,
      previews,
      images,
      watch,
      setValue,
      setState: setFlowProcess,
    }),
    onChangeUpload: (props: OnChangeFunctionProps) => onChangeUpload({
      watch,
      setValue,
      setState: setFlowProcess,
      setError,
      ...props,
    }),
  }
}
